// Free-tool cross-sell banners.
//
// Two standalone, deliberately eye-catching marketing islands that invite the
// visitor to try the other free Forge Ultra tools once they have finished
// running the current calculator. Rendered at the very BOTTOM of the ETF Wealth
// Builder and Financial Freedom pages so they never overlap the calculator
// results. Visual language intentionally mirrors the Drift Auditor's
// `ProRebalancingShowcase` (dark gradient card, decorative glow accents, badge
// eyebrow, metric/feature tiles, gradient CTA) for a cohesive premium feel.

import React from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight,
    Gauge,
    Layers,
    LineChart,
    PieChart,
    ScanSearch,
    ShieldCheck,
    Sparkles,
    TrendingDown,
} from 'lucide-react';

interface FeatureItem {
    icon: React.ReactNode;
    label: string;
}

interface PromoBannerProps {
    eyebrow: string;
    title: string;
    description: string;
    features: FeatureItem[];
    ctaLabel: string;
    to: string;
    // Tailwind class fragments that theme the individual banner.
    gradientClass: string;
    borderClass: string;
    shadowClass: string;
    glowOneClass: string;
    glowTwoClass: string;
    badgeClass: string;
    eyebrowIconClass: string;
    featureBorderClass: string;
    featureIconClass: string;
    ctaClass: string;
    ctaRingOffsetClass: string;
    trustIconClass: string;
}

const PromoBanner: React.FC<PromoBannerProps> = ({
    eyebrow,
    title,
    description,
    features,
    ctaLabel,
    to,
    gradientClass,
    borderClass,
    shadowClass,
    glowOneClass,
    glowTwoClass,
    badgeClass,
    eyebrowIconClass,
    featureBorderClass,
    featureIconClass,
    ctaClass,
    ctaRingOffsetClass,
    trustIconClass,
}) => (
    <div
        className={`relative overflow-hidden rounded-2xl border ${borderClass} shadow-xl ${shadowClass} ${gradientClass}`}
    >
        {/* Decorative glow accents */}
        <div
            className={`pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-full blur-3xl ${glowOneClass}`}
            aria-hidden="true"
        />
        <div
            className={`pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full blur-3xl ${glowTwoClass}`}
            aria-hidden="true"
        />

        <div className="relative p-6 sm:p-7 lg:p-8">
            {/* Eyebrow badge */}
            <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest ${badgeClass}`}
            >
                <Sparkles className={`h-3.5 w-3.5 ${eyebrowIconClass}`} />
                {eyebrow}
            </span>

            <h3 className="mt-4 text-xl sm:text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                {title}
            </h3>
            <p className="mt-2 max-w-2xl text-sm sm:text-[15px] text-slate-200/70 leading-relaxed">
                {description}
            </p>

            {/* Feature tiles */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {features.map((f) => (
                    <div
                        key={f.label}
                        className={`flex items-center gap-2.5 rounded-xl bg-white/[0.06] backdrop-blur-sm border ${featureBorderClass} px-3.5 py-3`}
                    >
                        <span className={`shrink-0 ${featureIconClass}`}>{f.icon}</span>
                        <span className="text-xs sm:text-[13px] font-semibold text-slate-100/90 leading-snug">
                            {f.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <Link
                    to={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm sm:text-base font-black tracking-wide shadow-lg transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${ctaRingOffsetClass} ${ctaClass}`}
                >
                    {ctaLabel}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <p className="inline-flex items-center gap-1.5 text-xs text-slate-200/60">
                    <ShieldCheck className={`h-4 w-4 ${trustIconClass}`} />
                    Free to use · No sign-up required
                </p>
            </div>
        </div>
    </div>
);

/**
 * Renders both cross-sell banners stacked vertically. Dropped in at the bottom
 * of a calculator page (inside the page's max-width container) so the banners
 * stay aligned with the calculator and never overlap the results above them.
 */
export const PromoBanners: React.FC = () => (
    <section
        aria-label="Explore more free Forge Ultra tools"
        className="max-w-7xl mx-auto px-4 lg:px-8 mt-12 sm:mt-16 space-y-6 sm:space-y-8"
    >
        <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                Go Deeper With Your Portfolio
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                Put your real holdings to the test with two more free, institutional-grade analyzers.
            </p>
        </div>

        <PromoBanner
            eyebrow="Free Tool"
            title="Portfolio Diversification Analyzer"
            description="Is your portfolio more concentrated than you think? In just a few moments, discover where your biggest exposures lie and whether your investments are truly working together to support a stronger, more resilient portfolio."
            features={[
                { icon: <PieChart className="h-5 w-5" />, label: 'Concentration & exposure map' },
                { icon: <Layers className="h-5 w-5" />, label: 'Cross-asset correlation insight' },
                { icon: <ScanSearch className="h-5 w-5" />, label: 'Hidden single-name risk' },
            ]}
            ctaLabel="Is My Portfolio Well-Diversified?"
            to="/free-tools/portfolio-diversification-checker"
            gradientClass="bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-950"
            borderClass="border-indigo-400/20"
            shadowClass="shadow-indigo-950/30"
            glowOneClass="bg-violet-500/20"
            glowTwoClass="bg-indigo-400/10"
            badgeClass="bg-gradient-to-r from-indigo-300 to-violet-400 text-indigo-950 shadow-sm"
            eyebrowIconClass="text-indigo-900"
            featureBorderClass="border-indigo-300/15"
            featureIconClass="text-indigo-300"
            ctaClass="bg-gradient-to-r from-indigo-300 via-indigo-400 to-violet-500 text-indigo-950 shadow-indigo-500/20 focus-visible:ring-indigo-300"
            ctaRingOffsetClass="focus-visible:ring-offset-slate-900"
            trustIconClass="text-indigo-300"
        />

        <PromoBanner
            eyebrow="Free Tool"
            title="Portfolio Drift & Rebalancing Auditor"
            description="Doing nothing has a cost. See how your allocation silently drifts over time, how much wealth the quiet volatility tax erodes, and whether disciplined rebalancing would have cut your drawdowns and tail risk."
            features={[
                { icon: <TrendingDown className="h-5 w-5" />, label: 'Silent allocation drift' },
                { icon: <Gauge className="h-5 w-5" />, label: 'Volatility-tax estimate' },
                { icon: <LineChart className="h-5 w-5" />, label: 'Rebalancing backtest' },
            ]}
            ctaLabel="Audit My Portfolio"
            to="/free-tools/portfolio-drift-auditor"
            gradientClass="bg-gradient-to-br from-slate-900 via-slate-900 to-sky-950"
            borderClass="border-sky-400/20"
            shadowClass="shadow-sky-950/30"
            glowOneClass="bg-sky-500/20"
            glowTwoClass="bg-emerald-400/10"
            badgeClass="bg-gradient-to-r from-sky-300 to-cyan-400 text-sky-950 shadow-sm"
            eyebrowIconClass="text-sky-900"
            featureBorderClass="border-sky-300/15"
            featureIconClass="text-sky-300"
            ctaClass="bg-gradient-to-r from-sky-300 via-sky-400 to-cyan-500 text-sky-950 shadow-sky-500/20 focus-visible:ring-sky-300"
            ctaRingOffsetClass="focus-visible:ring-offset-slate-900"
            trustIconClass="text-emerald-300"
        />
    </section>
);

export default PromoBanners;
